import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { safeSessionStorage } from '@/utils/storage';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  isWorker: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  isAdmin: false,
  isWorker: false,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session first
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        safeSessionStorage.removeItem('is_logging_in');
        setLoading(false);
      } else {
        const isOAuthCallback = window.location.hash.includes('access_token') || window.location.hash.includes('error_description');
        const isLoggingIn = safeSessionStorage.getItem('is_logging_in') === 'true';
        
        if (isOAuthCallback || isLoggingIn) {
          // Keep loading true while Supabase parses the URL and triggers onAuthStateChange
          // Fallback timeout to prevent infinite loading state
          setTimeout(() => setLoading(false), 2000);
        } else {
          setLoading(false);
        }
      }
    }).catch((err) => {
      console.error('Auth session fetch failed:', err);
      setLoading(false);
    });

    // Then listen for changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) safeSessionStorage.removeItem('is_logging_in');
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

    // Extract roles from JWT claims (Zero-latency role verification)
    const isAdmin = session?.user?.app_metadata?.is_admin === true;
    const isWorker = session?.user?.app_metadata?.is_worker === true;

  return (
    <AuthContext.Provider value={{ session, user: session?.user ?? null, loading, isAdmin, isWorker, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
